<script lang="ts">
  import UploadZone from '$lib/ui/UploadZone.svelte';
  import StepTree from '$lib/ui/StepTree.svelte';
  import StepParamsPanel from '$lib/ui/StepParamsPanel.svelte';
  import ProjectSettingsPanel from '$lib/ui/ProjectSettingsPanel.svelte';
  import PreviewCanvas from '$lib/ui/PreviewCanvas.svelte';
  import LayerList from '$lib/ui/LayerList.svelte';
  import ExportButton from '$lib/ui/ExportButton.svelte';
  import RecipeMenu from '$lib/ui/RecipeMenu.svelte';
  import Statusbar from '$lib/ui/Statusbar.svelte';
  import {
    isDirty,
    resetProject,
    selectStep,
  } from '$lib/state/project.svelte';
  import { deleteStep } from '$lib/model/tree';
  import { isVirtualNode } from '$lib/model/validity';
  import { triggerRecompute } from '$lib/compute/recompute.svelte';
  import { projectStore } from '$lib/state/project.svelte';
  import {
    decodeUpload,
    extractFromClipboardEvent,
    UploadRejected,
    filenameStem,
  } from '$lib/io/upload';
  import { createEmptyPipeline } from '$lib/model/tree';
  import {
    setActiveSource,
    computeStatus,
  } from '$lib/compute/recompute.svelte';

  function startOver() {
    if (
      isDirty() &&
      typeof window !== 'undefined' &&
      window.confirm
    ) {
      if (
        !window.confirm(
          'Discard the current upload and pipeline? This cannot be undone.',
        )
      ) {
        return;
      }
    }
    setActiveSource(null, null);
    resetProject();
  }
  import { pixelDimensions } from '$lib/compute/settings-utils';
  import { orientTargetForSource } from '$lib/compute/orient';

  interface Toast {
    id: number;
    kind: 'warn' | 'error' | 'info';
    text: string;
  }

  let toasts = $state<Toast[]>([]);
  let toastSeq = 0;
  let activeTab = $state<'pipeline' | 'settings'>('pipeline');

  function pushToast(t: Omit<Toast, 'id'>) {
    const id = ++toastSeq;
    toasts.push({ ...t, id });
    setTimeout(() => {
      toasts = toasts.filter((x) => x.id !== id);
    }, 6000);
  }

  async function handleClipboardPaste(e: ClipboardEvent) {
    const file = extractFromClipboardEvent(e);
    if (!file) return;
    e.preventDefault();
    try {
      const decoded = await decodeUpload(file);
      const stem = filenameStem(decoded.asset.filename);
      const fresh = createEmptyPipeline(stem);
      projectStore.name = stem;
      projectStore.pipeline = fresh;
      projectStore.selected_step_id = fresh.root_id;
      projectStore.input_asset = decoded.asset;
      projectStore.layer_order = [];
      const oriented = orientTargetForSource(
        decoded.bitmap.width,
        decoded.bitmap.height,
        projectStore.settings,
      );
      projectStore.settings.target_width = oriented.width;
      projectStore.settings.target_height = oriented.height;
      setActiveSource(decoded.bitmap, decoded.blob);
      if (decoded.classification.kind === 'warn') {
        pushToast({
          kind: 'warn',
          text: `Large image (${decoded.classification.megapixels.toFixed(1)} MP) — operations may be slow.`,
        });
      }
    } catch (err) {
      const text =
        err instanceof UploadRejected
          ? err.message
          : 'Paste failed — clipboard did not contain a supported image.';
      pushToast({ kind: 'error', text });
    }
  }

  $effect(() => {
    const listener = (e: ClipboardEvent) => void handleClipboardPaste(e);
    document.addEventListener('paste', listener);
    return () => document.removeEventListener('paste', listener);
  });

  let lastError = '';
  $effect(() => {
    if (computeStatus.error && computeStatus.error !== lastError) {
      lastError = computeStatus.error;
      pushToast({ kind: 'warn', text: computeStatus.error });
    }
  });

  // beforeunload warning when the project has been edited beyond the empty
  // baseline. Browsers will prompt the user before navigation.
  $effect(() => {
    function listener(e: BeforeUnloadEvent) {
      if (isDirty()) {
        e.preventDefault();
        e.returnValue = '';
      }
    }
    window.addEventListener('beforeunload', listener);
    return () => window.removeEventListener('beforeunload', listener);
  });

  // Global keyboard shortcuts: Delete = remove selected step (except root /
  // virtual nodes); Ctrl/Cmd+E = trigger export via a synthesized click on
  // the export button.
  $effect(() => {
    function isEditableTarget(t: EventTarget | null): boolean {
      if (!(t instanceof HTMLElement)) return false;
      const tag = t.tagName.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
      return t.isContentEditable;
    }

    function listener(e: KeyboardEvent) {
      if (isEditableTarget(e.target)) return;
      const cmd = e.ctrlKey || e.metaKey;

      if (e.key === 'Delete' || e.key === 'Backspace') {
        const id = projectStore.selected_step_id;
        if (!id) return;
        if (id === projectStore.pipeline.root_id) return;
        const step = projectStore.pipeline.steps[id];
        if (!step) return;
        if (isVirtualNode(step.type)) return;
        e.preventDefault();
        const removed = deleteStep(projectStore.pipeline, id);
        projectStore.layer_order = projectStore.layer_order.filter(
          (sid) => !removed.includes(sid),
        );
        selectStep(projectStore.pipeline.root_id);
        triggerRecompute();
      } else if (cmd && e.key.toLowerCase() === 'e') {
        e.preventDefault();
        const btn = document.querySelector<HTMLButtonElement>(
          'button.export',
        );
        if (btn && !btn.disabled) btn.click();
      }
    }
    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  });

  const dims = $derived(pixelDimensions(projectStore.settings));
</script>

<div class="shell">
  <header class="topbar">
    <span class="brand">silky2</span>
    {#if projectStore.input_asset}
      <span class="meta">
        {projectStore.name}
        <span class="dim">·</span>
        <span class="mono">{dims.width}×{dims.height}px</span>
      </span>
    {/if}
    <span class="topbar-spacer"></span>
    {#if computeStatus.running}
      <span class="status running">computing…</span>
    {/if}
    <RecipeMenu onMessage={(m) => pushToast(m)} />
    {#if projectStore.input_asset}
      <button class="reset" onclick={startOver} title="Start over with a new upload">
        ⟲ new
      </button>
      <ExportButton onMessage={(m) => pushToast(m)} />
    {/if}
  </header>

  <aside class="left">
    <div class="tabs">
      <button
        class:active={activeTab === 'pipeline'}
        onclick={() => (activeTab = 'pipeline')}
        >Pipeline</button
      >
      <button
        class:active={activeTab === 'settings'}
        onclick={() => (activeTab = 'settings')}
        >Settings</button
      >
    </div>
    {#if activeTab === 'pipeline'}
      {#if projectStore.input_asset}
        <StepTree />
      {:else}
        <p class="empty">Upload an image to begin.</p>
      {/if}
    {:else}
      <ProjectSettingsPanel />
    {/if}
  </aside>

  <main class="center">
    {#if projectStore.input_asset}
      <PreviewCanvas />
    {:else}
      <div class="upload-wrap">
        <UploadZone onMessage={(m) => pushToast(m)} />
      </div>
    {/if}
  </main>

  <aside class="right">
    <StepParamsPanel />
    {#if projectStore.input_asset}
      <hr />
      <LayerList />
    {/if}
  </aside>

  <Statusbar />

  <div class="toasts">
    {#each toasts as t (t.id)}
      <div class="toast" data-kind={t.kind}>{t.text}</div>
    {/each}
  </div>
</div>

<style>
  .shell {
    height: 100%;
    display: grid;
    grid-template-columns: 280px 1fr 320px;
    grid-template-rows: 40px 1fr 24px;
    grid-template-areas:
      'topbar topbar topbar'
      'left   center right'
      'status status status';
    position: relative;
  }
  .topbar {
    grid-area: topbar;
    display: flex;
    align-items: center;
    gap: var(--gap-m);
    padding: 0 var(--gap-l);
    background: var(--surface);
    border-bottom: 1px solid var(--border);
  }
  .brand {
    font-weight: 600;
    letter-spacing: 0.04em;
  }
  .meta {
    color: var(--text-dim);
    font-size: 12px;
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .meta .dim {
    opacity: 0.5;
  }
  .meta .mono {
    font-family: var(--font-mono);
  }
  .topbar-spacer {
    flex: 1;
  }
  .status.running {
    font-size: 11px;
    font-family: var(--font-mono);
    color: var(--accent);
  }
  .reset {
    font-size: 12px;
    padding: 6px 10px;
  }
  .left,
  .right {
    background: var(--surface);
    padding: var(--gap-m);
    overflow: auto;
  }
  .left {
    grid-area: left;
    border-right: 1px solid var(--border);
  }
  .right {
    grid-area: right;
    border-left: 1px solid var(--border);
    display: flex;
    flex-direction: column;
    gap: var(--gap-m);
  }
  .right hr {
    border: none;
    border-top: 1px solid var(--border);
    margin: 0;
  }
  .center {
    padding: 0;
  }
  .center {
    grid-area: center;
    background: var(--bg);
    display: grid;
    place-items: center;
    overflow: auto;
  }
  .upload-wrap {
    width: min(520px, 80%);
  }
  .tabs {
    display: flex;
    gap: 4px;
    margin-bottom: var(--gap-m);
  }
  .tabs button {
    flex: 1;
    font-size: 12px;
    padding: 6px;
    background: transparent;
  }
  .tabs button.active {
    background: var(--accent-dim);
    color: var(--bg);
    border-color: var(--accent);
  }
  .empty {
    color: var(--text-dim);
    font-size: 13px;
    margin: 0;
  }
  .toasts {
    position: absolute;
    bottom: var(--gap-l);
    right: var(--gap-l);
    display: flex;
    flex-direction: column;
    gap: var(--gap-s);
    z-index: 10;
  }
  .toast {
    padding: 8px 12px;
    border-radius: var(--radius);
    background: var(--surface-2);
    border: 1px solid var(--border);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    max-width: 360px;
    font-size: 13px;
  }
  .toast[data-kind='warn'] {
    border-color: var(--warn);
  }
  .toast[data-kind='error'] {
    border-color: var(--danger);
  }
</style>
